// @flow

import React, { PureComponent } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import styled from 'styled-components';
import { colors, fonts } from 'shared/styleguide';
import { i18n } from 'shared/utils';
import Panel from 'shared/components/common/Panel';
import EmptyListResults from 'shared/components/common/EmptyListResults';
import AddUpdateOrderYieldModal from 'main-app/components/AddUpdateOrderYieldModal';
import AddUpdateItemModal from 'main-app/components/AddUpdateItemModal';
import Button from 'shared/components/common/Button';
import DeleteOrderYieldModal from 'main-app/components/DeleteOrderYieldModal';
import OrderYieldPanels from 'shared/components/common/OrderYieldPanels';
import GenerateJobsFromOrderMutation from 'main-app/mutations/GenerateJobsFromOrder';
import * as Actions from 'main-app/store/Actions';
import type { OrderDetails_order as OrderFragment } from './__generated__/OrderDetails_order';
import type { OrderDetails_defaultForm as DefaultFormFragment } from './__generated__/OrderDetails_defaultForm';

type Props = {
  order: OrderFragment,
  defaultForm: DefaultFormFragment,
};

type State = {
  addOrderYieldModalOpen: boolean,
  addItemAndOrderYieldModalOpen: boolean,
  createAnotherItem: boolean,
  updateOrderYieldRecord: ?Object,
  deleteOrderYieldRecord: ?Object,
};

const SectionHeader = styled.div`
  ${fonts.headerBold};
  color: ${colors.charcoalGrey};
  padding: 24px 0 16px;
`;

const ButtonsContainer = styled.div`
  display: flex;

  button:first-of-type {
    margin-right: 16px;
  }
`;

const ButtonWrapper = styled.div`
  padding-bottom: 16px;
`;

class OrderDetails extends PureComponent<Props, State> {
  initialState = {
    addOrderYieldModalOpen: false,
    addItemAndOrderYieldModalOpen: false,
    createAnotherItem: false,
    updateOrderYieldRecord: null,
    deleteOrderYieldRecord: null,
  };

  constructor(props: Props) {
    super(props);

    this.state = this.initialState;
  }

  handleCloseAllModals = () => {
    this.setState(this.initialState);
  };

  handleOpenItemModal = () => {
    this.setState({
      addOrderYieldModalOpen: true,
    });
  };

  handleOpenEditItemModal = (orderYield: Object) => {
    this.setState({
      updateOrderYieldRecord: orderYield,
    });
  };

  handleOpenDeleteItemModal = (orderYield: Object) => {
    this.setState({
      deleteOrderYieldRecord: orderYield,
    });
  };

  handleOpenCreateItemModal = () => {
    this.setState({
      addItemAndOrderYieldModalOpen: true,
    });
  };

  handleReopenAddItemAndOrderYieldModal = () => {
    this.setState({ ...this.initialState, createAnotherItem: true }, () =>
      this.setState({
        addItemAndOrderYieldModalOpen: true,
      }),
    );
  };

  handleGenerateJobs = () => {
    const { order } = this.props;
    
    const confirmMessage = i18n.t(
      `Auto-generate job tickets for Order #${order.orderNumber}?`
    );

    if (!window.confirm(confirmMessage)) {
      return;
    }

    Actions.alertNotification(
      i18n.t('Generating job tickets...'),
      'info'
    );

    GenerateJobsFromOrderMutation.commit(
      { orderId: order.id, skipMaterialCheck: false },
      {
        onSuccess: response => {
          const result = response?.generateJobsFromOrder?.result;
          const count = result?.success || 0;
          const errors = result?.failed || 0;
          const skipped = result?.skipped || 0;
          
          if (count > 0) {
            const message = errors > 0 || skipped > 0
              ? i18n.t(`Generated ${count} job(s). ${errors} error(s), ${skipped} skipped`)
              : i18n.t(`Successfully generated ${count} job ticket(s)!`);
            
            Actions.alertNotification(message, errors > 0 ? 'warning' : 'success');
          } else if (errors > 0) {
            const firstLog = result.logs?.find(log => log.errorMessage);
            Actions.alertNotification(
              i18n.t(`Failed to generate jobs: ${firstLog?.errorMessage || 'Unknown error'}`),
              'error'
            );
          } else {
            Actions.alertNotification(
              i18n.t('No jobs were generated. All yields may already have jobs.'),
              'warning'
            );
          }
        },
        onFailure: error => {
          Actions.alertNotification(
            i18n.t(`Failed to generate jobs: ${error.message || 'Unknown error'}`),
            'error'
          );
        },
      }
    );
  };

  render() {
    const { order, defaultForm } = this.props;
    const {
      addOrderYieldModalOpen,
      deleteOrderYieldRecord,
      updateOrderYieldRecord,
      addItemAndOrderYieldModalOpen,
      createAnotherItem,
    } = this.state;
    const hasYields = order.yields?.edges?.length;

    return (
      <>
        <SectionHeader>{i18n.t('Items')}</SectionHeader>
        <ButtonsContainer>
          <ButtonWrapper>
            <Button
              theme="blue"
              width="auto"
              onClick={this.handleOpenItemModal}
            >
              {i18n.t('Add Item')}
            </Button>
          </ButtonWrapper>
          <ButtonWrapper>
            <Button
              theme="border-white"
              width="auto"
              onClick={this.handleOpenCreateItemModal}
            >
              {i18n.t('Create Item')}
            </Button>
          </ButtonWrapper>
          {hasYields && (
            <ButtonWrapper>
              <Button
                theme="green"
                width="auto"
                onClick={this.handleGenerateJobs}
              >
                {i18n.t('Auto-Generate Jobs')}
              </Button>
            </ButtonWrapper>
          )}
        </ButtonsContainer>
        {!hasYields ? (
          <Panel>
            <EmptyListResults
              graphic="items"
              message={i18n.t(
                'This Order is empty. Add an Item to get started.',
              )}
            />
          </Panel>
        ) : (
          <OrderYieldPanels
            history={history}
            orderYieldEdges={order.yields.edges}
            onEditItem={this.handleOpenEditItemModal}
            onDeleteItem={this.handleOpenDeleteItemModal}
          />
        )}
        {(addOrderYieldModalOpen || updateOrderYieldRecord) && (
          <AddUpdateOrderYieldModal
            orderId={order.id}
            orderYield={updateOrderYieldRecord}
            onClose={this.handleCloseAllModals}
            orderYieldEdges={order.yields.edges}
          />
        )}
        {deleteOrderYieldRecord && (
          <DeleteOrderYieldModal
            orderId={order.id}
            orderYield={deleteOrderYieldRecord}
            onClose={this.handleCloseAllModals}
          />
        )}
        {addItemAndOrderYieldModalOpen && (
          <AddUpdateItemModal
            onClose={this.handleCloseAllModals}
            onReopen={this.handleReopenAddItemAndOrderYieldModal}
            createAnotherItem={createAnotherItem}
            item={null}
            defaultForm={defaultForm}
            orderId={order.id}
          />
        )}
      </>
    );
  }
}

export default createFragmentContainer(OrderDetails, {
  order: graphql`
    fragment OrderDetails_order on Order {
      id
      yields(first: null)
        @connection(key: "OrderDetails_order_yields", filters: []) {
        edges {
          ...OrderYieldPanels_orderYieldEdges
          ...AddUpdateOrderYieldModal_orderYieldEdges
          node {
            id
            order {
              id
            }
            orderedQuantity
            item {
              id
              name
              description
              ...AddUpdateItemModal_item
            }
            ...AddUpdateOrderYieldModal_orderYield
            ...DeleteOrderYieldModal_orderYield
          }
        }
      }
      orderNumber
      customerOrderNumber
      orderedAt
      promisedAt
      requiredAt
    }
  `,
  defaultForm: graphql`
    fragment OrderDetails_defaultForm on Form {
      id
      ...AddUpdateItemModal_defaultForm
    }
  `,
});
